import { brand } from "@frontend/configs/brand.config";
import { env } from "@frontend/configs/env.config";
import { useQuery } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext, useEffect } from "react";
import { z } from "zod";

/**
 * Retailer branding. Name, logo and icon are read at RUNTIME from
 * `GET /api/brand/branding.json` (the backend env); build-time `VITE_APP_NAME`
 * / `VITE_BRAND_LOGO_URL` only cover first paint.
 *
 * Colours never come from the backend: they are the frontend's own env —
 * `/brand-colors.json`, written by the container entrypoint from
 * `VITE_BRAND_*_COLOR` at start-up, else the build-time values. It is fetched
 * with `cache: "no-store"`, so neither the browser cache nor a CDN in front
 * (Cloudflare's Browser Cache TTL overrides `no-cache` on static files) can
 * hand out a stale copy.
 *
 * The last answers are cached in localStorage so the next cold start (or an
 * offline one) paints the brand immediately.
 */

const brandingZod = z.object({
	appName: z.string(),
	logoUrl: z.string().nullable(),
	iconUrl: z.string().nullable(),
});
export type Branding = z.infer<typeof brandingZod> & {
	primaryColor: string | null;
	secondaryColor: string | null;
};

const CACHE_KEY = "branding.v1";
const COLORS_CACHE_KEY = "brandColors.v1";

const hex = z
	.string()
	.regex(/^#[0-9a-fA-F]{6}$/)
	.optional()
	.catch(undefined);
const brandColorsZod = z.object({ primary: hex, secondary: hex });
type BrandColors = z.infer<typeof brandColorsZod>;

const apiBase = env.VITE_API_URL || "";

/** Same-origin in production (nginx proxies /api); absolute in dev/e2e. */
export const brandAssetUrl = (path: string): string => `${apiBase}/api/brand/${path}`;

const fallback: Branding = {
	appName: brand.appName,
	logoUrl: brand.logoUrl,
	iconUrl: null,
	primaryColor: brand.primaryColor,
	secondaryColor: brand.secondaryColor,
};

const readCached = <T,>(key: string, schema: z.ZodType<T>): T | undefined => {
	try {
		const raw = localStorage.getItem(key);
		if (!raw) return undefined;
		const parsed = schema.safeParse(JSON.parse(raw));
		return parsed.success ? parsed.data : undefined;
	} catch {
		return undefined;
	}
};

const writeCached = (key: string, value: unknown) => {
	try {
		localStorage.setItem(key, JSON.stringify(value));
	} catch {
		// Storage full / blocked: the in-memory copy is enough.
	}
};

const fetchBrandColors = async (): Promise<BrandColors> => {
	const response = await fetch("/brand-colors.json", { cache: "no-store", credentials: "omit" });
	if (!response.ok) throw new Error(`brand colours ${response.status}`);
	const colors = brandColorsZod.parse(await response.json());
	writeCached(COLORS_CACHE_KEY, colors);
	return colors;
};

const fetchBranding = async (): Promise<z.infer<typeof brandingZod>> => {
	const response = await fetch(brandAssetUrl("branding.json"), { credentials: "omit" });
	if (!response.ok) throw new Error(`branding ${response.status}`);
	const branding = brandingZod.parse(await response.json());
	writeCached(CACHE_KEY, branding);
	return branding;
};

const BrandingContext = createContext<Branding>(fallback);

export function BrandingProvider({ children }: { children: (branding: Branding) => ReactNode }) {
	const { data } = useQuery({
		queryKey: ["branding"],
		queryFn: fetchBranding,
		initialData: () => readCached(CACHE_KEY, brandingZod),
		// Cached value paints first; always refresh it once per load.
		initialDataUpdatedAt: 0,
		staleTime: 5 * 60_000,
		retry: 1,
	});
	const { data: runtimeColors } = useQuery({
		queryKey: ["brand-colors"],
		queryFn: fetchBrandColors,
		initialData: () => readCached(COLORS_CACHE_KEY, brandColorsZod),
		initialDataUpdatedAt: 0,
		staleTime: 5 * 60_000,
		retry: 1,
	});
	const colors = {
		primaryColor: runtimeColors?.primary ?? brand.primaryColor,
		secondaryColor: runtimeColors?.secondary ?? brand.secondaryColor,
	};
	const branding: Branding = { ...(data ?? fallback), ...colors };

	useEffect(() => {
		document.title = branding.appName;
		const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
		if (meta && branding.primaryColor) meta.content = branding.primaryColor;
	}, [branding.appName, branding.primaryColor]);

	return <BrandingContext.Provider value={branding}>{children(branding)}</BrandingContext.Provider>;
}

export const useBranding = (): Branding => useContext(BrandingContext);
